import React, { useState } from "react"
import { AbsoluteFill, Html5Video, OffthreadVideo, Sequence, getRemotionEnvironment, useCurrentFrame, useVideoConfig } from "remotion"
import { Gif } from "@remotion/gif"
import { joinSplitPieces, msToFrames } from "../../utils/timing"
import type { Track } from "../../schemas"

interface GifTrackProps {
  track: Track
}

export const GifTrack: React.FC<GifTrackProps> = ({ track }) => {
  const { fps, width: compositionWidth, height: compositionHeight } = useVideoConfig()
  const { isPlayer } = getRemotionEnvironment()
  const frame = useCurrentFrame()
  // A GIF video that fails to load (expired link, 403, network) goes to the track's boundary, which
  // shows a Retry notice; before, it only logged and the GIF silently never appeared.
  const [mediaError, setMediaError] = useState<Error | null>(null)
  if (mediaError) throw mediaError

  const segments = joinSplitPieces(track.sections.flatMap(s => s.segments))

  return (
    <AbsoluteFill>
      {segments.map((segment) => {
        const inFrame = msToFrames(segment.timeline.in, fps)
        const duration = msToFrames(segment.timeline.out - segment.timeline.in, fps)
        const content = segment.content
        const mediaInfo = content?.mediaInfo
        const src = content?.url || mediaInfo?.originUrl || mediaInfo?.previewUrl
        if (!src) return null

        const position = segment.position ?? { xPct: 0, yPct: 0, wPct: 0.3, hPct: 0.3 }
        const left = `${50 + (position.xPct ?? 0) * 50}%`
        const top = `${50 + (position.yPct ?? 0) * 50}%`
        const rawWidth = mediaInfo?.width ?? content?.width
        const rawHeight = mediaInfo?.height ?? content?.height
        const targetWidth = Math.max(1, Math.round((position.wPct ?? 0.3) * compositionWidth))
        const targetHeight = Math.max(1, Math.round((position.hPct ?? 0.3) * compositionHeight))
        let width = targetWidth
        let height = targetHeight

        // Keep GIF proportions within the position box so preview and export match.
        if (
          typeof rawWidth === "number" &&
          rawWidth > 0 &&
          typeof rawHeight === "number" &&
          rawHeight > 0
        ) {
          const sourceAspect = rawWidth / rawHeight
          const targetAspect = targetWidth / targetHeight
          if (sourceAspect > targetAspect) {
            width = targetWidth
            height = Math.max(1, Math.round(targetWidth / sourceAspect))
          } else {
            height = targetHeight
            width = Math.max(1, Math.round(targetHeight * sourceAspect))
          }
        }

        // Keep GIF overlays visually balanced on portrait canvases.
        const maxWidth = Math.round(compositionWidth * 0.32)
        const maxHeight = Math.round(compositionHeight * 0.28)
        const widthScale = maxWidth > 0 ? Math.min(1, maxWidth / width) : 1
        const heightScale = maxHeight > 0 ? Math.min(1, maxHeight / height) : 1
        const clampScale = Math.min(widthScale, heightScale)
        width = Math.max(1, Math.round(width * clampScale))
        height = Math.max(1, Math.round(height * clampScale))

        // Keep GIF fully within frame based on its center point.
        const centerXPx = compositionWidth * (0.5 + (position.xPct ?? 0) * 0.5)
        const centerYPx = compositionHeight * (0.5 + (position.yPct ?? 0) * 0.5)
        const boundMaxWidth = Math.max(1, Math.round(2 * Math.min(centerXPx, compositionWidth - centerXPx)))
        const boundMaxHeight = Math.max(1, Math.round(2 * Math.min(centerYPx, compositionHeight - centerYPx)))
        if (width > boundMaxWidth || height > boundMaxHeight) {
          const overflowWidthScale = Math.min(1, boundMaxWidth / width)
          const overflowHeightScale = Math.min(1, boundMaxHeight / height)
          const overflowScale = Math.min(overflowWidthScale, overflowHeightScale)
          width = Math.max(1, Math.round(width * overflowScale))
          height = Math.max(1, Math.round(height * overflowScale))
        }
        const mediaType = String(mediaInfo?.mediaType ?? "").toLowerCase()
        const looksLikeGif = mediaType === "image" || src.toLowerCase().includes(".gif")
        const audioSettings = content?.audioSettings ?? {}
        const segmentDurationMs = Math.max(1, segment.timeline.out - segment.timeline.in)
        const currentTimeMs = (frame / fps) * 1000
        const elapsedMs = Math.max(0, currentTimeMs - segment.timeline.in)
        const baseVolume = Math.max(0, Math.min(1, Number(audioSettings.volume ?? 100) / 100))
        const fadeInMs = Math.max(0, Number(audioSettings.fadeInMs ?? 0))
        const fadeOutMs = Math.max(0, Number(audioSettings.fadeOutMs ?? 0))
        const fadeInFactor = fadeInMs > 0 ? Math.min(1, elapsedMs / fadeInMs) : 1
        const remainingMs = Math.max(0, segment.timeline.out - currentTimeMs)
        const fadeOutFactor = fadeOutMs > 0 ? Math.min(1, remainingMs / fadeOutMs) : 1
        const effectiveVolume = baseVolume * Math.min(fadeInFactor, fadeOutFactor)
        // The right half of a split GIF carries on from where the left half stopped.
        const trimFrames = msToFrames(segment.trimStartMs ?? 0, fps) || undefined

        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={Math.max(duration, 1)} premountFor={isPlayer ? fps : 0}>
            <AbsoluteFill>
              <div
                style={{
                  position: "absolute",
                  left,
                  top,
                  transform: "translate(-50%, -50%)",
                }}
              >
                {looksLikeGif ? (
                  <Gif src={src} width={width} height={height} />
                ) : (
                  isPlayer ? (
                    <Html5Video
                      src={src}
                      pauseWhenBuffering
                      volume={effectiveVolume}
                      trimBefore={trimFrames}
                      style={{ width, height, objectFit: "cover" }}
                      onError={setMediaError}
                    />
                  ) : (
                    <OffthreadVideo
                      src={src}
                      trimBefore={trimFrames}
                      volume={(f) => {
                        const tMs = (f / fps) * 1000
                        const elapsed = Math.max(0, tMs - segment.timeline.in)
                        const base = Math.max(0, Math.min(1, Number(audioSettings.volume ?? 100) / 100))
                        const inMs = Math.max(0, Number(audioSettings.fadeInMs ?? 0))
                        const outMs = Math.max(0, Number(audioSettings.fadeOutMs ?? 0))
                        const inFactor = inMs > 0 ? Math.min(1, elapsed / inMs) : 1
                        const remMs = Math.max(0, segment.timeline.out - tMs)
                        const outFactor = outMs > 0 ? Math.min(1, remMs / outMs) : 1
                        return base * Math.min(inFactor, outFactor)
                      }}
                      style={{ width, height, objectFit: "cover" }}
                    />
                  )
                )}
              </div>
            </AbsoluteFill>
          </Sequence>
        )
      })}
    </AbsoluteFill>
  )
}
